import { evalAuthHeaders, getEvalBase } from '../../evalApiBase'
import { EvalRunAccepted } from '../Runs/runsAdminModel'
import {
  buildPresetsListQuery,
  EvalRunPreset,
  EvalRunPresetCreateInput,
  EvalRunPresetListResponse,
  EvalRunPresetUpdateInput,
  PresetFilters,
  PresetLaunchOverrides,
} from './presetsAdminModel'

async function readErrorDetail(res: Response): Promise<string | null> {
  try {
    const body = await res.json()
    if (body && typeof body.detail === 'string') {
      return body.detail
    }
  } catch {
    // fall through
  }
  return null
}

export async function listPresets(
  filters: PresetFilters,
): Promise<EvalRunPresetListResponse> {
  const query = buildPresetsListQuery(filters)
  const res = await fetch(`${getEvalBase()}/v1/eval-run-presets?${query}`, {
    headers: evalAuthHeaders(),
  })
  if (!res.ok) {
    throw new Error(`Presets list failed: ${res.status}`)
  }
  return res.json()
}

export async function createPreset(
  input: EvalRunPresetCreateInput,
): Promise<EvalRunPreset> {
  const res = await fetch(`${getEvalBase()}/v1/eval-run-presets`, {
    method: 'POST',
    headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Preset create failed: ${res.status}`)
  }
  return res.json()
}

export async function updatePreset(
  input: EvalRunPresetUpdateInput,
): Promise<EvalRunPreset> {
  const { preset_id, ...payload } = input
  const res = await fetch(`${getEvalBase()}/v1/eval-run-presets/${preset_id}`, {
    method: 'PATCH',
    headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Preset update failed: ${res.status}`)
  }
  return res.json()
}

export async function deletePreset(presetId: string): Promise<void> {
  const res = await fetch(`${getEvalBase()}/v1/eval-run-presets/${presetId}`, {
    method: 'DELETE',
    headers: evalAuthHeaders(),
  })
  if (!res.ok) {
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Preset delete failed: ${res.status}`)
  }
}

export async function launchPreset(
  presetId: string,
  overrides: PresetLaunchOverrides,
): Promise<EvalRunAccepted> {
  const res = await fetch(
    `${getEvalBase()}/v1/eval-run-presets/${presetId}/run`,
    {
      method: 'POST',
      headers: { ...evalAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(overrides),
    },
  )
  if (!res.ok) {
    // An empty selection (or one matching no cases) returns 422 with an
    // actionable message — surface it like the run-create flow does.
    const detail = await readErrorDetail(res)
    throw new Error(detail || `Preset launch failed: ${res.status}`)
  }
  return res.json()
}
